import { createFileRoute, redirect } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { Layout } from "@/components/layout"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { StoryReader } from "@/components/story-reader"
import { ReaderHeader } from "@/components/reader-settings"
import { StoryGenerationPanel } from "@/components/admin/story-generation-panel"
import { getAdminStory } from "@/lib/api/admin-stories"
import { useFuriganaPreference } from "@/hooks/use-furigana-preference"
import { useTranslationPreference } from "@/hooks/use-translation-preference"

export const Route = createFileRoute("/_authenticated/admin/stories/$id")({
  beforeLoad: ({ context }) => {
    const flags = (context as { userFlags?: { isAdmin?: boolean | null } })
      .userFlags
    if (!flags?.isAdmin) {
      throw redirect({ to: "/stories" })
    }
  },
  component: AdminStory,
})

function AdminStory() {
  const { id } = Route.useParams()
  const { mode, setMode, firstOnly, setFirstOnly } = useFuriganaPreference()
  const { showTranslation, setShowTranslation } = useTranslationPreference()

  const {
    data: story,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["admin-story", id],
    queryFn: () => getAdminStory(id),
  })

  return (
    <Layout>
      <section className="pb-12">
        <ReaderHeader
          showSettings={Boolean(story)}
          hasTranslation={Boolean(story?.translation)}
          mode={mode}
          onModeChange={setMode}
          firstOnly={firstOnly}
          onFirstOnlyChange={setFirstOnly}
          showTranslation={showTranslation}
          onShowTranslationChange={setShowTranslation}
        />
        {isPending && (
          <div className="space-y-4">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-9 w-3/4" />
            <Skeleton className="h-7 w-44" />
            <Card>
              <CardContent>
                <div className="space-y-3 py-2">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-5/6" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-4/5" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {isError && (
          <p className="text-sm text-destructive">Failed to load story.</p>
        )}
        {story && (
          <article>
            <StoryReader
              title={story.title}
              subtitle={story.subtitle}
              audio={story.audio}
              html={story.html}
              translation={story.translation ?? null}
              mode={mode}
              firstOnly={firstOnly}
              showTranslation={showTranslation}
            />
            <StoryGenerationPanel id={id} />
          </article>
        )}
      </section>
    </Layout>
  )
}
